import { api } from './client';

// ==================== Types ====================

export type LlmProvider = 'OPENAI' | 'GEMINI';
export type LlmServiceTier = 'STANDARD' | 'FLEX' | 'BATCH' | 'PRIORITY';
export type LlmMessageRole = 'USER' | 'ASSISTANT' | 'SYSTEM';
export type LlmPresetVectorStoreMode = 'DEFAULT_ON' | 'REQUIRED';

export interface LlmPresetVectorStore {
  id: string;
  presetId: string;
  storeId: string;
  mode: LlmPresetVectorStoreMode;
  sortOrder: number;
  store: LlmUsableVectorStore;
}

export interface LlmPreset {
  id: string;
  name: string;
  description: string | null;
  provider: LlmProvider;
  model: string;
  serviceTier: LlmServiceTier;
  systemPrompt: string | null;
  temperature: number;
  maxTokens: number | null;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  vectorStores?: LlmPresetVectorStore[];
}

export interface LlmMessageImage {
  id: string;
  filename: string;
  originalName: string;
  size: number;
  mimeType: string;
  width: number | null;
  height: number | null;
}

export interface UrlCitation {
  type: 'url_citation';
  url: string;
  title: string;
  start_index: number;
  end_index: number;
}

export type LlmAnnotation = UrlCitation | {
  type?: string;
  start_index?: number;
  end_index?: number;
  file_id?: string;
  filename?: string;
  quote?: string;
  text?: string;
  [key: string]: unknown;
};

export interface LlmVectorStoreMaintainer {
  id: string;
  employeeId: string;
  employee: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
  canWriteDocuments?: boolean;
  canRunSync?: boolean;
}

export interface LlmUsableVectorStore {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  enabled: boolean;
  status: string;
  openaiVectorStoreId: string | null;
  defaultStrictness: string;
  lastRunAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  fileCount: number;
  usageBytes: string | number | null;
  maintainers: LlmVectorStoreMaintainer[];
  canMaintain: boolean;
  canRunSync: boolean;
}

export interface LlmConversationVectorStore {
  id: string;
  conversationId: string;
  storeId: string;
  enabled: boolean;
  store: LlmUsableVectorStore;
}

export interface LlmMessageVectorStore {
  id: string;
  messageId: string;
  storeId: string;
  openaiVectorStoreId: string;
  metadata: Record<string, unknown> | null;
  store: LlmUsableVectorStore;
}

export interface LlmMessage {
  id: string;
  conversationId: string;
  role: LlmMessageRole;
  content: string;
  images: LlmMessageImage[];
  inputTokens: number | null;
  outputTokens: number | null;
  cachedTokens: number | null;
  model: string | null;
  serviceTier: LlmServiceTier | null;
  durationMs: number | null;
  isStreaming: boolean;
  annotations: LlmAnnotation[] | null;
  vectorStores?: LlmMessageVectorStore[];
  finishReason: string | null;
  createdAt: string;
}

export interface LlmConversation {
  id: string;
  title: string | null;
  userId: string;
  employeeId: string | null;
  presetId: string | null;
  preset: LlmPreset | null;
  provider: LlmProvider;
  model: string;
  messages: LlmMessage[];
  vectorStores?: LlmConversationVectorStore[];
  createdAt: string;
  updatedAt: string;
}

export interface LlmConversationListItem {
  id: string;
  title: string | null;
  provider: LlmProvider;
  model: string;
  createdAt: string;
  updatedAt: string;
  preset: { name: string } | null;
  _count: { messages: number };
}

export interface ConversationsListResponse {
  items: LlmConversationListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LlmPricing {
  provider: LlmProvider;
  model: string;
  serviceTier: LlmServiceTier;
  inputPricePerMillion: number;
  cachedInputPricePerMillion: number | null;
  outputPricePerMillion: number;
  effectiveFrom: string;
}

export interface UsageStats {
  model: string;
  serviceTier: LlmServiceTier;
  messageCount: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  totalCostUsd: number;
}

export interface UserUsageSummary {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCachedTokens: number;
  totalCostUsd: number;
  byModel: UsageStats[];
}

export interface UploadedImage {
  id: string;
  filename: string;
  originalName: string;
  size: number;
  mimeType: string;
  width: number;
  height: number;
}

// ==================== API ====================

export const llmApi = {
  // Status
  getStatus: async (): Promise<{ available: boolean }> => {
    const { data } = await api.get('/llm/status');
    return data;
  },

  // Presets
  getPresets: async (): Promise<LlmPreset[]> => {
    const { data } = await api.get('/llm/presets');
    return data;
  },

  // Conversations
  createConversation: async (params: {
    presetId?: string;
    title?: string;
    vectorStoreIds?: string[];
  }): Promise<LlmConversation> => {
    const { data } = await api.post('/llm/conversations', params);
    return data;
  },

  getConversations: async (params?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ConversationsListResponse> => {
    const { data } = await api.get('/llm/conversations', { params });
    return data;
  },

  getConversation: async (id: string): Promise<LlmConversation> => {
    const { data } = await api.get(`/llm/conversations/${id}`);
    return data;
  },

  updateConversation: async (
    id: string,
    params: { title: string }
  ): Promise<LlmConversation> => {
    const { data } = await api.patch(`/llm/conversations/${id}`, params);
    return data;
  },

  deleteConversation: async (id: string): Promise<void> => {
    await api.delete(`/llm/conversations/${id}`);
  },

  // Images
  uploadImage: async (file: File): Promise<UploadedImage> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post('/llm/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  // Returns relative path for use with AuthImage component
  getImagePath: (imageId: string): string => {
    return `/llm/images/${imageId}`;
  },

  getUsableVectorStores: async (): Promise<{ items: LlmUsableVectorStore[] }> => {
    const { data } = await api.get('/llm/vector-stores/usable');
    return data;
  },

  // Pricing
  getPricing: async (): Promise<LlmPricing[]> => {
    const { data } = await api.get('/llm/pricing');
    return data;
  },

  // Usage
  getUsage: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<UserUsageSummary> => {
    const { data } = await api.get('/llm/usage', { params });
    return data;
  },

  /**
   * Continue conversation by creating a new chat with summarized context
   * Used when a conversation hits the token limit
   */
  continueConversation: async (conversationId: string): Promise<LlmConversation> => {
    const { data } = await api.post(`/llm/conversations/${conversationId}/continue`);
    return data;
  },
};
